import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal } from "./Modal";
import { useT, useLp } from "../lib/i18n";
import { useSession } from "../lib/auth";
import { saveSession } from "../lib/auth";
import {
  confirm as apiConfirm,
  createCheckoutSession,
  formatError,
  login as apiLogin,
  signup as apiSignup,
  type CheckoutCycle,
} from "../lib/api";
import { OAUTH_ENABLED, startGoogleLogin } from "../lib/cognito";
import {
  M1_PLAUSIBLE_EVENTS,
  trackPlausibleEvent,
  trackPlausibleEventBeforeRedirect,
} from "../lib/plausible";
import { executeRecaptcha } from "../lib/recaptcha";
import { attributionProps, getAttribution } from "../lib/attribution";
import { trackLaunchSignupOnce } from "../lib/launch-funnel";

type View = "login" | "signup" | "confirm" | "done";
type Plan = "free" | "pro";

interface Flow {
  email?: string;
  plan?: Plan;
  cycle?: CheckoutCycle;
  /** `checkout` means: after successful login, redirect to Stripe. */
  next?: string;
}

function parsePlan(value: string | null): Plan {
  return value === "pro" ? "pro" : "free";
}

function parseCheckoutCycle(value: string | null): CheckoutCycle | undefined {
  return value === "monthly" || value === "annual" || value === "founding100"
    ? value
    : undefined;
}

/**
 * Single modal for the entire auth flow. Views transition internally
 * (signup → confirm → done) so the user never leaves the overlay. URL is
 * the canonical trigger for opening in `login` or `signup` — internal
 * transitions don't touch the URL.
 *
 * The `confirm` view locks the modal (dismissable=false) so an accidental
 * ESC or backdrop click doesn't lose the pending-confirmation context —
 * the only way forward is submitting the 6-digit code or using the
 * "change email" link back to signup.
 */
export function AuthModal() {
  const [params, setParams] = useSearchParams();
  const t = useT();
  const lp = useLp();
  const session = useSession();

  const [view, setView] = useState<View | null>(null);
  const [flow, setFlow] = useState<Flow>({});

  // URL params open the modal in login / signup. Internal flows (confirm,
  // done) are not URL-driven — they transition from within the component.
  useEffect(() => {
    const authParam = params.get("auth");
    if (authParam === "login") {
      setView("login");
      setFlow({
        next: params.get("next") ?? undefined,
        cycle: parseCheckoutCycle(params.get("cycle")),
      });
    } else if (authParam === "signup") {
      setView("signup");
      setFlow({
        plan: parsePlan(params.get("plan")),
        cycle: parseCheckoutCycle(params.get("cycle")),
      });
    } else if (!authParam) {
      // Only honor a URL-driven close if we're not mid-flow. `confirm` and
      // `done` are stateful transitions the URL shouldn't cancel.
      setView((v) => (v === "confirm" || v === "done" ? v : null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const clearAuthParams = () => {
    const next = new URLSearchParams(params);
    next.delete("auth");
    next.delete("plan");
    next.delete("next");
    next.delete("cycle");
    setParams(next, { replace: true });
  };

  const close = () => {
    setView(null);
    setFlow({});
    clearAuthParams();
  };

  // After signup submits successfully, lock the user into the confirm step.
  const goConfirm = (email: string) => {
    setFlow((f) => ({ ...f, email }));
    setView("confirm");
    clearAuthParams();
  };

  // After confirm submits successfully, unlock and show the success view.
  const goDone = () => {
    setView("done");
  };

  // "Change email" link on confirm → go back to signup view.
  const goSignup = () => {
    setView("signup");
  };

  // Switch from login → signup without closing the modal.
  const goLogin = () => {
    setView("login");
  };

  if (!view) return null;

  const titles: Record<View, string> = {
    login: t.auth.login.heading,
    signup: flow.plan === "pro" ? t.auth.signup.headingPro : t.auth.signup.headingFree,
    confirm: t.auth.confirm.heading,
    done: t.auth.done.heading,
  };

  return (
    <Modal
      open
      onClose={close}
      title={titles[view]}
      dismissable={view !== "confirm"}
    >
      {view === "login" && (
        <LoginView
          flow={flow}
          onSuccess={close}
          onSwitchToSignup={goSignup}
        />
      )}
      {view === "signup" && (
        <SignupView
          flow={flow}
          onSuccess={(email) => goConfirm(email)}
        />
      )}
      {view === "confirm" && (
        <ConfirmView
          flow={flow}
          onSuccess={goDone}
          onChangeEmail={goSignup}
        />
      )}
      {view === "done" && (
        <DoneView
          flow={flow}
          session={session}
          onNeedLogin={goLogin}
          onClose={close}
          lpPrefix={lp}
        />
      )}
    </Modal>
  );
}

// -----------------------------------------------------------------------------
//  LOGIN
// -----------------------------------------------------------------------------

function LoginView({
  flow,
  onSuccess,
  onSwitchToSignup,
}: {
  flow: Flow;
  onSuccess: () => void;
  onSwitchToSignup: () => void;
}) {
  const t = useT();
  const tl = t.auth.login;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (!email.includes("@")) return setError(tl.errorEmail);
    if (!password) return setError(tl.errorPassword);

    setSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha("login");
      const tokens = await apiLogin(email, password, recaptchaToken);
      saveSession(tokens);

      // Pro signup chained through login — go straight to Stripe afterwards.
      if (flow.next === "checkout") {
        try {
          const attr = getAttribution();
          const { url } = await createCheckoutSession(tokens.access_token, {
            cycle: flow.cycle,
            ...attr,
          });
          trackPlausibleEventBeforeRedirect(
            M1_PLAUSIBLE_EVENTS.checkoutStarted,
            () => window.location.assign(url),
            attributionProps(),
          );
          return;
        } catch {
          // Stripe unreachable — fall through to the regular close so the
          // user lands somewhere sensible.
        }
      }
      onSuccess();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <p className="text-sm text-ink/60 dark:text-paper/60">
        {tl.subheading}
      </p>

      <GoogleAuthBlock
        next={flow.next}
        onError={setError}
        disabled={submitting}
      />

      <form onSubmit={handleSubmit} className={`${OAUTH_ENABLED ? "mt-4" : "mt-6"} space-y-4`}>
        <Field label={tl.emailLabel} type="email" required value={email} onChange={setEmail} autoComplete="email" inputMode="email" />
        <Field label={tl.passwordLabel} type="password" required value={password} onChange={setPassword} autoComplete="current-password" />

        {error && <ErrorBox>{error}</ErrorBox>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-graphite text-paper hover:bg-graphite-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-5 py-3 text-sm font-medium"
        >
          {submitting ? tl.submitting : tl.submit}
        </button>

        <p className="text-center text-xs text-ink/50 dark:text-paper/50">
          {tl.noAccount}{" "}
          <button
            type="button"
            onClick={onSwitchToSignup}
            className="text-ink dark:text-paper underline underline-offset-2"
          >
            {tl.createFree}
          </button>
        </p>
      </form>
    </>
  );
}

// -----------------------------------------------------------------------------
//  SIGNUP
// -----------------------------------------------------------------------------

function SignupView({ flow, onSuccess }: { flow: Flow; onSuccess: (email: string) => void }) {
  const t = useT();
  const ts = t.auth.signup;
  const plan = flow.plan ?? "free";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordHint = password.length > 0 && password.length < 8;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (!email.includes("@")) return setError(ts.errorEmail);
    if (password.length < 8) return setError(ts.errorPassword);

    setSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha("signup");
      const attr = getAttribution();
      await apiSignup({ email, password, firstName, lastName, recaptchaToken, ...attr });
      if (plan === "free") trackPlausibleEvent(M1_PLAUSIBLE_EVENTS.signupFree, attributionProps());
      trackLaunchSignupOnce(`modal.${plan}.${email.trim().toLowerCase()}`);
      onSuccess(email);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Pro signup chained through Google → resume Stripe checkout after the
  // OAuth round-trip, same as the email/password path does post-confirm.
  const oauthNext = plan === "pro" ? "checkout" : undefined;

  return (
    <>
      <StepIndicator current={1} total={2} label={ts.stepLabel} />
      <p className="mt-2 text-sm text-ink/60 dark:text-paper/60">
        {plan === "pro" ? ts.subPro : ts.subFree}
      </p>

      <GoogleAuthBlock
        next={oauthNext}
        cycle={flow.cycle}
        signupPlan={plan === "free" ? "free" : undefined}
        onError={setError}
        disabled={submitting}
      />

      <form onSubmit={handleSubmit} className={`${OAUTH_ENABLED ? "mt-4" : "mt-6"} space-y-4`}>
        <div className="grid grid-cols-2 gap-3">
          <Field label={ts.firstNameLabel} value={firstName} onChange={setFirstName} autoComplete="given-name" />
          <Field label={ts.lastNameLabel} value={lastName} onChange={setLastName} autoComplete="family-name" />
        </div>

        <Field label={ts.emailLabel} type="email" required value={email} onChange={setEmail} autoComplete="email" inputMode="email" />

        <Field
          label={ts.passwordLabel}
          type="password"
          required
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          hint={passwordHint ? ts.passwordWarn : ts.passwordHint}
          hintTone={passwordHint ? "warning" : "muted"}
        />

        {error && <ErrorBox>{error}</ErrorBox>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-graphite text-paper hover:bg-graphite-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-5 py-3 text-sm font-medium"
        >
          {submitting ? ts.submitting : ts.submit}
        </button>

        <p className="text-center text-xs text-ink/50 dark:text-paper/50">
          {ts.codeNote}
        </p>
      </form>
    </>
  );
}

// -----------------------------------------------------------------------------
//  CONFIRM
// -----------------------------------------------------------------------------

function ConfirmView({
  flow,
  onSuccess,
  onChangeEmail,
}: {
  flow: Flow;
  onSuccess: () => void;
  onChangeEmail: () => void;
}) {
  const t = useT();
  const tc = t.auth.confirm;
  const email = flow.email ?? "";

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) return setError(tc.errorCode);
    if (!email) return setError(tc.errorEmail);

    setSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha("confirm_signup");
      await apiConfirm({ email, code: trimmed, recaptchaToken });
      onSuccess();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <StepIndicator current={2} total={2} label={tc.stepLabel} />
      <p className="mt-2 text-sm text-ink/60 dark:text-paper/60">
        {tc.subPre}{" "}
        <span className="text-ink dark:text-paper font-medium">
          {email || tc.yourEmail}
        </span>
        {tc.subPost}
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <label className="block">
          <span className="block text-xs font-mono uppercase tracking-wider text-ink/50 dark:text-paper/50 mb-1.5">
            {tc.codeLabel}
          </span>
          <input
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="block w-full rounded-lg bg-transparent border border-ink/15 dark:border-paper/15 px-3 py-3 text-center font-mono text-2xl tracking-[0.3em] text-ink dark:text-paper focus:outline-none focus:border-graphite transition-colors"
          />
        </label>

        {error && <ErrorBox>{error}</ErrorBox>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-graphite text-paper hover:bg-graphite-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-5 py-3 text-sm font-medium"
        >
          {submitting ? tc.submitting : tc.submit}
        </button>

        <div className="flex items-center justify-between text-xs text-ink/50 dark:text-paper/50">
          <button
            type="button"
            onClick={onChangeEmail}
            className="hover:text-ink dark:hover:text-paper"
          >
            {tc.changeEmail}
          </button>
          <span>{tc.codeExpiry}</span>
        </div>
      </form>
    </>
  );
}

// -----------------------------------------------------------------------------
//  DONE
// -----------------------------------------------------------------------------

function DoneView({
  flow,
  session,
  onNeedLogin,
  onClose,
  lpPrefix,
}: {
  flow: Flow;
  session: ReturnType<typeof useSession>;
  onNeedLogin: () => void;
  onClose: () => void;
  lpPrefix: string;
}) {
  const t = useT();
  const td = t.auth.done;
  const tbp = t.billing.proPurchase;
  const plan = flow.plan ?? "free";

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCompletePurchase = async () => {
    if (busy) return;
    setError(null);
    if (!session) {
      // User must log in first so we have an access token for Stripe.
      onNeedLogin();
      return;
    }
    setBusy(true);
    try {
      const attr = getAttribution();
      const { url } = await createCheckoutSession(session.tokens.access_token, {
        cycle: flow.cycle,
        ...attr,
      });
      trackPlausibleEventBeforeRedirect(
        M1_PLAUSIBLE_EVENTS.checkoutStarted,
        () => window.location.assign(url),
        attributionProps(),
      );
    } catch {
      setError(tbp.error);
      setBusy(false);
    }
  };

  return (
    <div className="text-center">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-done/15 text-done">
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
          <polyline points="4,10 8,14 16,6" />
        </svg>
      </div>

      <p className="mt-6 text-sm text-ink/60 dark:text-paper/60">
        {plan === "pro" ? td.subPro : td.subFree}
      </p>

      <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
        {plan === "pro" ? (
          <button
            type="button"
            onClick={handleCompletePurchase}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-graphite text-paper hover:bg-graphite-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-6 py-3 text-sm font-medium"
          >
            {busy ? tbp.ctaLoading : session ? tbp.cta : tbp.ctaSignIn}
          </button>
        ) : (
          <a
            href={lpPrefix + "/downloads"}
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-graphite text-paper hover:bg-graphite-muted transition-colors px-6 py-3 text-sm font-medium"
          >
            {td.downloadCta}
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center rounded-full border border-ink/20 dark:border-paper/30 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
        >
          {td.homeCta}
        </button>
      </div>

      {error && <p className="mt-4 text-sm text-rec">{error}</p>}

      {plan === "pro" && !session && (
        <p className="mt-4 text-xs text-ink/50 dark:text-paper/50">
          {tbp.signedOut}
        </p>
      )}

      {plan === "pro" && (
        <div className="mt-8 rounded-xl border border-margin dark:border-paper/10 p-5 text-left">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink/50 dark:text-paper/50">
            {td.proNextStepsLabel}
          </div>
          <ul className="mt-3 space-y-2 text-sm text-ink/70 dark:text-paper/70">
            {td.proNextSteps.map((step) => (
              <li key={step}>• {step}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-8 text-xs text-ink/50 dark:text-paper/50">
        {td.troubleNote}
      </p>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Shared
// -----------------------------------------------------------------------------

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  hint?: string;
  hintTone?: "muted" | "warning";
}

function Field({ label, value, onChange, type = "text", required, autoComplete, inputMode, hint, hintTone = "muted" }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-wider text-ink/50 dark:text-paper/50 mb-1.5">
        {label}
      </span>
      <input
        type={type}
        required={required}
        autoComplete={autoComplete}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-lg bg-transparent border border-ink/15 dark:border-paper/15 px-3 py-2.5 text-ink dark:text-paper placeholder:text-ink/40 dark:placeholder:text-paper/40 focus:outline-none focus:border-graphite transition-colors"
      />
      {hint && (
        <span className={`mt-1.5 block text-[11px] ${hintTone === "warning" ? "text-rec" : "text-ink/50 dark:text-paper/50"}`}>
          {hint}
        </span>
      )}
    </label>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-rec/30 bg-rec/5 px-3 py-2 text-sm text-rec">
      {children}
    </div>
  );
}

function StepIndicator({ current, total, label }: { current: number; total: number; label: string }) {
  const t = useT();
  return (
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink/50 dark:text-paper/50">
      {t.auth.shell.step(current, total, label)}
    </div>
  );
}

// Federated sign-in entry point. Same control whether the user is on the
// login or signup view — Cognito's federation creates the user on first
// access and signs them in on subsequent visits, so a single button covers
// both. `next` propagates the post-auth intent (e.g. `checkout` to chain
// straight into Stripe after a Pro signup) through sessionStorage so
// /auth/callback can pick it up.
function GoogleAuthBlock({
  next,
  cycle,
  signupPlan,
  onError,
  disabled,
}: {
  next?: string;
  cycle?: CheckoutCycle;
  signupPlan?: "free";
  onError: (msg: string) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const tl = t.auth.login;

  if (!OAUTH_ENABLED) return null;

  const handleClick = async () => {
    if (disabled) return;
    try {
      if (next) sessionStorage.setItem("minuta.oauth.next", next);
      else sessionStorage.removeItem("minuta.oauth.next");
      if (cycle) sessionStorage.setItem("minuta.oauth.checkout_cycle", cycle);
      else sessionStorage.removeItem("minuta.oauth.checkout_cycle");
      if (signupPlan) sessionStorage.setItem("minuta.oauth.signup_plan", signupPlan);
      else sessionStorage.removeItem("minuta.oauth.signup_plan");
      await startGoogleLogin();
      // startGoogleLogin redirects — execution doesn't return.
    } catch (err) {
      onError(formatError(err));
    }
  };

  return (
    <div className="mt-6 space-y-4">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-ink/15 dark:border-paper/15 hover:bg-ink/5 dark:hover:bg-paper/5 disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-5 py-3 text-sm font-medium text-ink dark:text-paper"
      >
        <GoogleIcon />
        {tl.continueGoogle}
      </button>
      <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-ink/40 dark:text-paper/40">
        <span className="h-px flex-1 bg-ink/10 dark:bg-paper/10" />
        <span>{tl.orDivider}</span>
        <span className="h-px flex-1 bg-ink/10 dark:bg-paper/10" />
      </div>
    </div>
  );
}

// Multi-color "G" — Google's brand SVG, simplified to the 4 paths needed.
function GoogleIcon() {
  return (
    <svg viewBox="0 0 18 18" className="h-4 w-4" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.614z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
